/**
 * Wizard card for the stripe_connect step (Slice 1).
 *
 * "Generate Connect link" invokes the onboard-stripe-connect edge function,
 * which returns an OAuth authorize URL to send the client and flips the task
 * to waiting_on_client. When the client approves, the stripe-connect-callback
 * edge function stores the connected account id and marks the task done —
 * the run page's polling picks that up without a refresh. "Re-check" re-runs
 * the idempotent step (confirms an existing connection or issues a new link).
 */
import { useState } from 'react'
import { CheckCircle2, Copy, Link as LinkIcon, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface StripeConnectCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface StripeConnectResult {
  connectUrl?: string
  connectedAccountId?: string
}

export function StripeConnectCard({
  task,
  accountId,
  runId,
}: StripeConnectCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)
  const [copied, setCopied] = useState(false)

  const result = (task.result ?? {}) as StripeConnectResult
  const isConnected = task.status === 'done' && result.connectedAccountId
  const hasLink = Boolean(result.connectUrl) && !isConnected

  const runStep = () => {
    invokeStep.mutate(
      { functionName: 'onboard-stripe-connect', accountId },
      {
        onError: (error) =>
          toast({
            title: 'Stripe Connect step failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  const copyLink = async () => {
    if (!result.connectUrl) return
    await navigator.clipboard.writeText(result.connectUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          If OAuth fails, the client can still use the legacy key-based setup:
          have them enter their Stripe keys under Settings &gt; Integrations
          &gt; Stripe in their dashboard. Connect can be retried later — the
          step is safe to re-run.
        </p>
      }
    >
      {isConnected ? (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span>
            Connected:{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {result.connectedAccountId}
            </code>
          </span>
        </div>
      ) : (
        <>
          {hasLink && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Send this link to the client — one click in their Stripe
                account authorizes the platform. No keys are shared.
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={result.connectUrl}
                  className="font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button type="button" variant="outline" size="icon" onClick={copyLink}>
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={hasLink ? 'outline' : 'default'}
              onClick={runStep}
              disabled={invokeStep.isPending}
            >
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : hasLink ? (
                <RefreshCw className="mr-2 h-4 w-4" />
              ) : (
                <LinkIcon className="mr-2 h-4 w-4" />
              )}
              {hasLink ? 'Re-check / regenerate' : 'Generate Connect link'}
            </Button>
          </div>
        </>
      )}
    </StepCard>
  )
}
